<template>
  <div class="gs-region-address">
    <GsRegionSelect
      class="gs-region-address__region"
      :value="path"
      :placeholder="regionPlaceholder"
      :clearable="clearable"
      :disabled="disabled"
      @change="onRegionChange"
    />
    <el-input
      class="gs-region-address__detail"
      :model-value="detail"
      :placeholder="detailPlaceholder"
      :disabled="disabled"
      clearable
      @update:model-value="onDetailInput"
    />
  </div>
</template>

<script>
/**
 * 统一的「地点」输入：县级行政区划选择 + 详细地址。
 * v-model 为合并后的字符串（「省/市/区 详细地址」），与后端单列 officeAddress / address 兼容。
 * 选地区与填详细地址任一项都会重新合并；只有详细地址时原样输出，不会丢历史值。
 */
import GsRegionSelect from '@/components/GsRegionSelect/index.vue'
import { loadRegionTree } from '@/utils/region'

function flatten(nodes, out) {
  ;(nodes || []).forEach((node) => {
    out.set(String(node.value), node.label)
    if (node.children && node.children.length) flatten(node.children, out)
  })
  return out
}

export default {
  name: 'GsRegionAddress',
  components: { GsRegionSelect },
  props: {
    value: { type: String, default: '' },
    modelValue: { type: String, default: undefined },
    regionPlaceholder: { type: String, default: '请选择省 / 市 / 区' },
    detailPlaceholder: { type: String, default: '详细地址（街道、门牌等）' },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      path: [],
      detail: '',
      labels: new Map(),
      syncing: false
    }
  },
  computed: {
    current() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    }
  },
  watch: {
    current(val) {
      if (this.syncing) return
      const text = String(val || '')
      if (!text) {
        this.path = []
        this.detail = ''
        return
      }
      // 已经和内部状态一致（自己 emit 出去的），不再回填，避免把地区清掉
      if (text === this.compose()) return
      this.path = []
      this.detail = text
    }
  },
  created() {
    this.detail = String(this.current || '')
    loadRegionTree().then((tree) => {
      this.labels = flatten(tree || [], new Map())
    }).catch(() => {})
  },
  methods: {
    compose() {
      const names = (this.path || []).map((code) => this.labels.get(String(code))).filter(Boolean)
      const region = names.join('/')
      const detail = String(this.detail || '').trim()
      if (region && detail) return region + ' ' + detail
      return region || detail
    },
    emitValue() {
      const next = this.compose()
      this.syncing = true
      this.$emit('input', next)
      this.$emit('update:modelValue', next)
      this.$nextTick(() => { this.syncing = false })
    },
    onRegionChange(val) {
      this.path = Array.isArray(val) ? val : []
      this.emitValue()
    },
    onDetailInput(val) {
      this.detail = String(val == null ? '' : val)
      this.emitValue()
    }
  }
}
</script>

<style scoped>
.gs-region-address {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}
.gs-region-address__region {
  flex: 0 0 46%;
  min-width: 0;
}
.gs-region-address__detail {
  flex: 1 1 auto;
  min-width: 0;
}
@media (max-width: 960px) {
  .gs-region-address {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
  .gs-region-address__region {
    flex: 1 1 auto;
  }
}
</style>
